import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notification } from 'antd';
import { format } from 'date-fns';
import { set } from 'lodash';

import { useConfigurationStore } from '@store/use-configuration-store';
import { useUserStore } from '@store/use-user-store';

import type { ApiError, Registrant, VismaConfiguration } from '../types';
import { apis } from './api/Api';

export const useGetConfiguration = (
  friendlyUrl: string,
  module: string,
  enabled = true
) => {
  const { selectedLang } = useUserStore();
  return useQuery({
    queryKey: ['configuration', friendlyUrl, module, selectedLang],
    queryFn: () =>
      apis
        .getConfiguration(friendlyUrl, module)
        .then((response) => response.data),
    enabled: !!friendlyUrl && !!module && enabled
  });
};

export const useVismaConfiguration = () => {
  const { friendly_url, module } = useConfigurationStore();
  const query = useGetConfiguration(friendly_url, module);

  const configuration = useMemo<VismaConfiguration | null>(() => {
    if (!query.data) return null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const groupedSettings: Record<string, any> = {};
    query.data?.configuration?.settings?.forEach(
      ({ page, key, ...rest }: any) => {
        set(groupedSettings, [page, key], rest);
      }
    );

    const gi = groupedSettings?.['general-information'];
    const dp = groupedSettings?.['data-protection'];
    const pd = groupedSettings?.['personal-data'];
    const el = groupedSettings?.elearning;
    const id = groupedSettings?.['info-data'];
    const et = groupedSettings?.['elearning-test'];

    const generalInformation = {
      color_primary: gi?.color_primary?.value,
      color_primary_text: gi?.color_primary_text?.value,
      color_secondary_text: gi?.color_secondary_text?.value,
      button_next: gi?.button_next?.value,
      button_cancel: gi?.button_cancel?.value,
      button_finish: gi?.button_finish?.value,
      button_multi: gi?.button_multi?.value,
      is_multi: gi?.is_multi?.value,
      company_logo: gi?.company_logo?.image,
      button_quit: gi?.button_quit?.value,
      button_continue: gi?.button_continue?.value,
      button_close: gi?.button_close?.value,
      button_scan: gi?.button_scan?.value,
      button_new_registration: gi?.button_new_registration?.value
    };

    const login = {
      isActive: groupedSettings?.login?.module_status?.value === 'active',
      module_status: groupedSettings?.login?.module_status,
      featured_image: groupedSettings?.login?.featured_image?.image
    };

    const intro = {
      isActive: groupedSettings?.intro?.module_status?.value === 'active',
      module_status: groupedSettings?.intro?.module_status,
      title: groupedSettings?.intro?.title,
      description: groupedSettings?.intro?.description,
      button_single: groupedSettings?.intro?.button_single,
      button_multi: groupedSettings?.intro?.button_multi,
      featured_image: groupedSettings?.intro?.featured_image
    };

    const dataProtection = {
      isActive: dp?.module_status?.value === 'active',
      module_status: dp?.module_status,
      title: dp?.title?.value,
      description: dp?.description?.value,
      document_description: dp?.document_description?.value,
      document_link: dp?.document_link?.value,
      document: dp?.document?.image,
      featured_image: dp?.featured_image?.image,
      expiryOptions: [
        {
          value: dp?.['options.0.value']?.value,
          text: dp?.['options.0.text']?.value,
          decline: dp?.['options.0.decline']?.value === '1'
        },
        {
          value: dp?.['options.1.value']?.value,
          text: dp?.['options.1.text']?.value,
          decline: dp?.['options.1.decline']?.value === '1'
        },
        {
          value: dp?.['options.2.value']?.value,
          text: dp?.['options.2.text']?.value,
          decline: dp?.['options.2.decline']?.value === '1'
        },
        {
          value: dp?.['options.3.value']?.value,
          text: dp?.['options.3.text']?.value,
          decline: dp?.['options.3.decline']?.value === '1'
        }
      ].filter((option) => option.decline || option.value)
    };

    const personalData = {
      isActive: pd?.module_status?.value === 'active',
      title: pd?.title?.value,
      description: pd?.description?.value,
      mrz_module: { isActive: pd?.mrz_module?.value === 'active' },
      scan_popup_title: pd?.scan_popup_title?.value,
      scan_popup_description: pd?.scan_popup_description?.value,
      scan_popup_image: pd?.scan_popup_image?.image,
      personal_data_warning: pd?.personal_data_warning?.value,
      data_field: {
        fullname: {
          required: pd?.['data_field.name.mode']?.value === 'required'
        },
        birth_date: {
          required: pd?.['data_field.birth_date.mode']?.value === 'required'
        },
        document_type: {
          required: pd?.['data_field.document_type.mode']?.value === 'required'
        },
        document_number: {
          required:
            pd?.['data_field.document_number.mode']?.value === 'required'
        },
        document_expiry_date: {
          required:
            pd?.['data_field.document_expiry_date.mode']?.value === 'required'
        },
        mother_name: {
          required: pd?.['data_field.mother_name.mode']?.value === 'required',
          disabled: pd?.['data_field.mother_name.mode']?.value === 'disabled'
        },
        phonenumber: {
          required: pd?.['data_field.phonenumber.mode']?.value === 'required',
          disabled: pd?.['data_field.phonenumber.mode']?.value === 'disabled'
        },
        company_name: {
          required: pd?.['data_field.company_name.mode']?.value === 'required',
          disabled: pd?.['data_field.company_name.mode']?.value === 'disabled'
        }
      },
      multi: {
        visitor_list_title: pd?.['multi.visitor_list_title']?.value,
        visitor_list_accept: pd?.['multi.visitor_list_accept']?.value,
        visitor_list_empty: pd?.['multi.visitor_list_empty']?.value,
        button_add_person: pd?.['multi.button_add_person']?.value,
        button_add_new_person: pd?.['multi.button_add_new_person']?.value,
        button_video: pd?.['multi.button_video']?.value
      }
    };

    const elearningTestData = {
      compliance_test_has: et?.compliance_test_has?.value,
      title: et?.title?.value,
      description: et?.description?.value,
      button_retry: et?.button_retry?.value,
      toaster: {
        successful_title: et?.['toaster.successful_title']?.value,
        successful_message: et?.['toaster.successful_message']?.value,
        failed_title: et?.['toaster.failed_title']?.value,
        failed_message: et?.['toaster.failed_message']?.value
      }
    };

    const elearning = {
      isActive: el?.module_status?.value === 'active',
      module_status: el?.module_status?.value,
      title: el?.title?.value,
      description: el?.description?.value,
      elearning_type: el?.elearning_type?.value,
      elearning_video_url: el?.elearning_video_url?.value,
      elearning_accept: el?.elearning_accept?.value,
      elearning_decline: el?.elearning_decline?.value,
      featured_image: el?.featured_image?.image,
      decline_popup_title: el?.decline_popup_title?.value,
      decline_popup_description: el?.decline_popup_description?.value,
      compliance_test_has: el?.compliance_test_has?.value,
      compliance_test_wrong_answers_limit:
        el?.compliance_test_wrong_answers_limit?.value,
      multi: {
        visitor_list_title: el?.['multi.visitor_list_title']?.value,
        visitor_list_description: el?.['multi.visitor_list_description']?.value,
        entry_rules: el?.['multi.entry_rules']?.value,
        wait_for_video: el?.['multi.wait_for_video']?.value,
        button_accept: el?.['multi.button_accept']?.value,
        button_decline: el?.['multi.button_decline']?.value
      }
    };

    const infoData = {
      successful_title: id?.successful_title?.value,
      successful_description: id?.successful_description?.value,
      failed_title: id?.failed_title,
      failed_description: id?.failed_description,
      featured_image: id?.featured_image?.image
    };

    return {
      friendly_url,
      module,
      locales: query.data?.base?.locales ?? [],
      generalInformation,
      login,
      intro,
      dataProtection,
      personalData,
      elearning,
      elearningTestData,
      infoData
    } as VismaConfiguration;
  }, [friendly_url, module, query.data]);

  return { ...query, configuration };
};

export const useCreateVisitors = () => {
  const { friendly_url, module } = useConfigurationStore();
  return useMutation({
    mutationFn: (registrants: Registrant[]) => {
      const promises = registrants.map((r) =>
        apis.createVisitor(friendly_url, module, {
          documentType: r.documentType,
          name: r.fullname,
          documentNumber: r.docNo,
          birthDate: r.dateOfBirth
            ? format(r.dateOfBirth.toDate(), 'yyyy-MM-dd')
            : null,
          documentExpiryDate: r.docExpiryDate
            ? format(r.docExpiryDate.toDate(), 'yyyy-MM-dd')
            : null,
          phonenumber: r.phoneNumber,
          companyName: r.companyName,
          expiry: r.expiry
        })
      );
      return Promise.all(promises);
    },
    onError: (err: ApiError) => {
      notification.error({
        message: err?.response?.data?.errorMessage ?? 'Something went wrong',
        placement: 'topRight'
      });
    }
  });
};
